<script setup>
import {
    computed, onMounted, ref, watch,
} from "vue";

import VueGoogleAutocomplete from "vue-google-autocomplete";
import { isEmpty } from "lodash";
import CustomCheckbox from "@/components/CustomCheckbox";
import GoogleMap from "@/components/GoogleMap";
import InputField from "@/components/forms/InputField.vue";
import { validateUKPostcode, validValue } from "@/components/methods";

const props = defineProps({
    default: {
        type: Object,
        default: () => {
        },
    },
});

const distance = [
    "4 miles",
    "5 miles",
    "6 miles",
    "8 miles",
    "10 miles",
    "15 miles",
    "20 miles",
    "25 miles",
];

const emit = defineEmits(["update"]);

const mapZoom = ref(10);
const hasLocation = ref(false);
const isMobile = ref(false);

const enterManually = ref(false);

const venueData = ref({
    venueName: "",
    address1: "",
    address2: "",
    area: "",
    city: "",
    postcode: "",
    lat: "",
    lng: "",
    autocomplete: {},
    country: "United Kingdom",
});

const venueAutocompleteError = ref("");
const venueDataError = ref({});

const venueMobile = ref({
    venueName: "",
    address1: "",
    address2: "",
    area: "",
    city: "",
    postcode: "",
    lat: "",
    lng: "",
    autocomplete: {},
    travelDistance: "4 miles",
    country: "United Kingdom",
});

const venueMobileError = ref("");

const showLocationForm = ref(false);
const showMobileForm = ref(false);
const advancedForm = ref(false);
const showSecondPart = ref(false);

const mobileServiceSameVenue = ref("yes");

const editFirstVenue = ref(false);

// Components refs
const map = ref(null);
const autocompleteMobile = ref(null);
const autocompleteLocation = ref(null);

function switchManualAddress() {
    if (venueData.value.autocomplete !== "") {
        enterManually.value = true;
    }
}

function showSecondPartForm() {
    // Hide form for entering venue name etc.
    enterManually.value = false;

    // Form for hasLocation
    showLocationForm.value = false;

    // Showing second part if both checkbox selected
    showSecondPart.value = true;

    // Form for isMobile
    showMobileForm.value = true;

    // Set marker on the map
    setTimeout(() => {
        if (mobileServiceSameVenue.value === "yes") {
            map.value.setSingleMarker();
        }

        if (
            autocompleteMobile.value !== null
            && autocompleteMobile.value !== undefined
            && venueMobile.value.autocomplete !== undefined
            && venueMobile.value.autocomplete !== null
        ) {
            autocompleteMobile.value.update(
                Object.values(venueMobile.value.autocomplete).join(", "),
            );
            map.value.setSingleMarker();
        }
    }, 500);
}

function validateLocation() {
    return (
        validValue(venueData.value.venueName)
        && validValue(venueData.value.address1)
        && validValue(venueData.value.area)
        && venueData.value.area !== "N/A"
        && validValue(venueData.value.city)
        && validValue(venueData.value.postcode)
        && validateUKPostcode(venueData.value.postcode)
    );
}

function showErrors() {
    venueDataError.value.venueName = !validValue(venueData.value.venueName)
        ? "Enter venue name"
        : "";

    venueDataError.value.address1 = !validValue(venueData.value.address1) ? "Enter an address" : "";

    venueDataError.value.area = !validValue(venueData.value.area) || venueData.value.area === "N/A" ? "Enter an area" : "";

    venueDataError.value.city = !validValue(venueData.value.city) ? "Enter a city" : "";

    const poostcodeError = !validValue(venueData.value.postcode) ? "Enter a postcode" : "";

    venueDataError.value.postcode = validateUKPostcode(venueData.value.postcode)
        ? poostcodeError
        : "Enter valid postcode";
}

function validateMobile() {
    if (
        mobileServiceSameVenue.value === "yes"
        || venueMobile.value.autocomplete !== ""
    ) {
        venueMobileError.value = "";
    } else {
        venueMobileError.value = "Please select postcode";
    }

    return venueMobileError.value === "";
}

function store() {
    enterManually.value = false;
    const settings = {
        showLocationForm: showLocationForm.value,
        showMobileForm: showMobileForm.value,
        advancedForm: advancedForm.value,
        showSecondPart: showSecondPart.value,
        hasLocation: hasLocation.value,
        isMobile: isMobile.value,
        enterManually: enterManually.value,
        mobileServiceSameVenue: mobileServiceSameVenue.value,
        editFirstVenue: editFirstVenue.value,
    };

    emit("update", {
        venueData: venueData.value,
        venueMobile: venueMobile.value,
        settings,
    });
}

function editVenue() {
    enterManually.value = true;
    editFirstVenue.value = true;
}

function getAddressData(addressData, placeResultData, id) {
    // Update autocomplete data for location venue in case if only location selected and for both checkboxes selected
    if (
        (hasLocation.value && !isMobile.value)
        || (hasLocation.value && isMobile.value)
        || (hasLocation.value && isMobile.value && mobileServiceSameVenue.value === "yes")
    ) {
        const autocomplete = { ...addressData };
        delete autocomplete.latitude;
        delete autocomplete.longitude;
        venueData.value.autocomplete = autocomplete;
        venueData.value.lat = addressData.latitude;
        venueData.value.lng = addressData.longitude;
        // map.value.getParsedAddress(venueData.value.lat, venueData.value.lng);

        venueData.value.address1 = addressData.route;
        venueData.value.area = "";
        venueData.value.city = addressData.locality;
        venueData.value.postcode = addressData.postal_code;
    }
}

function getAddressDataMobile(addressData, placeResultData, id) {
    // Update autocomplete data if we set only mobile venue data
    if (!hasLocation.value && isMobile.value) {
        venueMobile.value.autocomplete = addressData;
        venueMobile.value.lat = addressData.latitude;
        venueMobile.value.lng = addressData.longitude;
        map.value.getParsedAddress(venueMobile.value.lat, venueMobile.value.lng);
    }

    // Update autocomplete data if we set different address for mobile venue
    if (hasLocation.value && isMobile.value && mobileServiceSameVenue.value === "no") {
        venueMobile.value.autocomplete = addressData;
        venueMobile.value.lat = addressData.latitude;
        venueMobile.value.lng = addressData.longitude;
        map.value.getParsedAddress(venueMobile.value.lat, venueMobile.value.lng);
    }
}

function updateLatLng({
    lat, lng, address, autocomplete,
}) {
    if (
        (hasLocation.value && !isMobile.value)
        || (hasLocation.value && isMobile.value && showLocationForm.value)
    ) {
        venueData.value.lat = lat();
        venueData.value.lng = lng();

        venueData.value.venueName = "";
        venueData.value.address1 = address.address;
        venueData.value.area = address.county;
        venueData.value.city = address.city;
        venueData.value.postcode = address.postcode;
        venueData.value.autocomplete = autocomplete;
        autocompleteLocation.value?.update(autocomplete);
    }

    if ((!hasLocation.value && isMobile.value) || showMobileForm.value) {
        venueMobile.value.lat = lat();
        venueMobile.value.lng = lng();

        venueMobile.value.venueName = "";
        venueMobile.value.address1 = address.address;
        venueMobile.value.area = address.county;
        venueMobile.value.city = address.city;
        venueMobile.value.postcode = address.postcode;
        venueMobile.value.autocomplete = autocomplete;
        autocompleteMobile.value?.update(autocomplete);
    }
}

function addressUpdated(address) {
    if (
        (hasLocation.value && !isMobile.value)
        || (hasLocation.value && isMobile.value && showLocationForm.value)
    ) {
        venueData.value.autocomplete = address;
        venueData.value.address1 = address.address;
        venueData.value.area = address.county;
        venueData.value.city = address.city;
        venueData.value.postcode = address.postcode ?? "";
    }

    // if ((!hasLocation.value && isMobile.value) || showMobileForm.value) {
    //     venueMobile.value.autocomplete = address;
    //     venueMobile.value.address1 = address.address;
    //     venueMobile.value.area = address.county;
    //     venueMobile.value.city = address.city;
    //     venueMobile.value.postcode = address.postcode ?? "";
    // }
}

const getLat = computed(() => {
    if (showSecondPart.value && venueMobile.value) {
        return venueMobile.value.lat;
    }
    if ((hasLocation.value && !isMobile.value) || (hasLocation.value && isMobile.value)) {
        return venueData.value.lat;
    }
    if (isMobile.value && !hasLocation.value) {
        return venueMobile.value.lat;
    }

    return 0;
});

const getLng = computed(() => {
    if (showSecondPart.value && venueMobile.value) {
        return venueMobile.value.lng;
    }
    if ((hasLocation.value && !isMobile.value) || (hasLocation.value && isMobile.value)) {
        return venueData.value.lng;
    }
    if (isMobile.value && !hasLocation.value) {
        return venueMobile.value.lng;
    }

    return 0;
});

const getDistance = computed(() => {
    if (hasLocation.value && !isMobile.value) {
        return "";
    }
    if (!hasLocation.value && isMobile.value) {
        return venueMobile.value.travelDistance;
    }

    if (showSecondPart.value) {
        return venueMobile.value.travelDistance;
    }

    return "";
});

function setLocationAutocomplete() {
    if (
        autocompleteLocation.value !== undefined
        && autocompleteLocation.value !== null
        && venueData.value.autocomplete !== undefined
    ) {
        autocompleteLocation.value.update(Object.values(venueData.value.autocomplete).join(", "));
    }

    setTimeout(() => {
        if (map.value !== undefined && map.value !== null && hasLocation.value) {
            map.value.setSingleMarker();
        }
    });
}

function setMobileAutocomplete() {
    if (
        autocompleteMobile.value !== undefined
        && autocompleteMobile.value !== null
        && venueMobile.value.autocomplete !== undefined
    ) {
        autocompleteMobile.value.update(Object.values(venueMobile.value.autocomplete).join(", "));
        if (map.value !== undefined && map.value !== null) {
            map.value.setSingleMarker();
        }
    }
}

watch(
    () => hasLocation.value,
    () => {
        if (hasLocation.value && isMobile.value) {
            advancedForm.value = true;
            showLocationForm.value = true;
            if (venueData.value?.autocomplete) {
                setTimeout(
                    () => autocompleteLocation.value?.update(
                        Object.values(venueData.value.autocomplete).join(", "),
                    ),
                    300,
                );
            }
        } else {
            showLocationForm.value = hasLocation.value;
            advancedForm.value = false;
            showMobileForm.value = isMobile.value;
            enterManually.value = false;
        }

        if (hasLocation.value && !isMobile.value) {
            setTimeout(() => {
                setLocationAutocomplete();
            }, 300);
        }

        if (!hasLocation.value && isMobile.value) {
            setTimeout(() => {
                setMobileAutocomplete();
            }, 500);
        }
    },
);

watch(
    () => venueData.value.autocomplete,
    () => setLocationAutocomplete(),
);

watch(
    () => venueMobile.value.autocomplete,
    () => setMobileAutocomplete(),
);

function setMapZoom(radius) {
    let zoom = 10;
    if (radius === 4 || radius === 5) {
        zoom = 11;
    }
    if (radius === 6) {
        zoom = 10;
    }

    if (radius > 8) {
        zoom = 9;
    }

    if (map.value !== undefined && map.value !== null && hasLocation.value) {
        mapZoom.value = zoom;
        setTimeout(() => {
            map.value?.setSingleMarker();
        });
    }
}

watch(
    () => venueMobile.value.travelDistance,
    () => {
        if (venueMobile.value.travelDistance === undefined) {
            venueMobile.value.travelDistance = "4 miles";
        }
        const radius = parseInt(venueMobile.value.travelDistance.replace(" miles", ""), 10);
        setMapZoom(radius);
    },
    {
        deep: true,
    },
);

watch(
    () => isMobile.value,
    () => {
        if (hasLocation.value && isMobile.value) {
            advancedForm.value = true;
        } else {
            showMobileForm.value = isMobile.value;
            advancedForm.value = false;
            showSecondPart.value = false;
            showLocationForm.value = hasLocation.value;

            setTimeout(() => {
                setLocationAutocomplete();
            }, 300);
        }

        if (isMobile.value) {
            setTimeout(() => {
                setMobileAutocomplete();
                if (
                    venueMobile.value.travelDistance === undefined
                    || venueMobile.value.travelDistance === "null miles"
                ) {
                    venueMobile.value.travelDistance = "4 miles";
                }

                // const radius = parseInt(venueMobile.value.travelDistance.replace(" miles", ""), 10);
                // setMapZoom(radius);
            }, 500);
        }
    },
);

function selectAll() {
    hasLocation.value = true;
    isMobile.value = true;
}

defineExpose({
    store,
    validateLocation,
    validateMobile,
    switchManualAddress,
    showSecondPartForm,
    showErrors,
    editFirstVenue,
    advancedForm,
    enterManually,
    showSecondPart,
    showLocationForm,
    isMobile,
    mobileServiceSameVenue,
    hasLocation,
    venueData,
    venueMobile,
});

function setDefaults() {
    if (!isEmpty(props.default.settings)) {
        showLocationForm.value = props.default.settings.showLocationForm;
        showMobileForm.value = props.default.settings.showMobileForm;
        advancedForm.value = props.default.settings.advancedForm;
        showSecondPart.value = props.default.settings.showSecondPart;
        mobileServiceSameVenue.value = props.default.settings.mobileServiceSameVenue;
        editFirstVenue.value = props.default.settings.editFirstVenue;
        hasLocation.value = props.default.settings.hasLocation;
        isMobile.value = props.default.settings.isMobile;
    }

    if (!isEmpty(props.default?.venueData)) {
        venueData.value = props.default.venueData;
        venueData.value.lat = parseFloat(props.default.venueData.lat);
        venueData.value.lng = parseFloat(props.default.venueData.lng);
        setLocationAutocomplete();
    }

    if (!isEmpty(props.default?.venueMobile)) {
        venueMobile.value = props.default.venueMobile;
        venueMobile.value.lat = parseFloat(props.default.venueMobile.lat);
        venueMobile.value.lng = parseFloat(props.default.venueMobile.lng);
        setMobileAutocomplete();
    }
}

onMounted(() => {
    setDefaults();
});

watch(
    () => props.default,
    () => {
        setDefaults();
    },
);
</script>
<template>
    <div>
        <div @click="selectAll" class="mt-3 text-underline select-all">Select all that apply</div>

        <div class="">
            <CustomCheckbox
                class="mt-4"
                @checkbox-click="hasLocation = !hasLocation"
                :checked="hasLocation"
            >
                <div class="d-flex flex-row">
                    <div class="checkbox-main-text mr-2">My service has a venue</div>
                    <div class="checkbox-sub-text">(i.e. Customers come to me)</div>
                </div>
            </CustomCheckbox>

            <CustomCheckbox class="mt-4" @checkbox-click="isMobile = !isMobile" :checked="isMobile">
                <div class="d-flex flex-row">
                    <div class="checkbox-main-text mr-2">My service is mobile</div>
                    <div class="checkbox-sub-text">(i.e. I travel to customers)</div>
                </div>
            </CustomCheckbox>
        </div>

        <div class="d-flex flex-column mt-4">
            <div v-if="hasLocation && isMobile && !showSecondPart" class="has-location-and-mobile">
                Venue for my service
            </div>

            <div v-if="!editFirstVenue">
                <div class="mt-3 venue-item" v-if="showSecondPart">
                    <div class="actions">
                        <div @click="editVenue" class="edit">
                            <svg
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M1.57784 11.5044C1.49691 11.5042 1.41689 11.4873 1.34288 11.4545C1.26886 11.4218 1.20247 11.374 1.14792 11.3142C1.09239 11.2549 1.05001 11.1846 1.02357 11.1078C0.997133 11.031 0.987228 10.9495 0.994506 10.8685L1.13742 9.29705L7.73959 2.69722L9.80284 4.75988L3.20242 11.3591L1.63092 11.502C1.61328 11.5037 1.59556 11.5045 1.57784 11.5044ZM10.2147 4.34747L8.15201 2.2848L9.38926 1.04755C9.44343 0.993313 9.50777 0.950286 9.57858 0.920931C9.6494 0.891575 9.72531 0.876465 9.80196 0.876465C9.87862 0.876465 9.95453 0.891575 10.0253 0.920931C10.0962 0.950286 10.1605 0.993313 10.2147 1.04755L11.4519 2.2848C11.5062 2.33897 11.5492 2.40331 11.5785 2.47413C11.6079 2.54494 11.623 2.62085 11.623 2.69751C11.623 2.77417 11.6079 2.85007 11.5785 2.92089C11.5492 2.9917 11.5062 3.05604 11.4519 3.11022L10.2153 4.34688L10.2147 4.34747Z"
                                    fill="#260045"
                                />
                            </svg>
                            Edit
                        </div>
                    </div>
                    <div class="title">{{ venueData.venueName }} {{ ", " + venueData.area }}</div>
                    <div class="mt-2 address">
                        Address: {{ venueData.address1 }}, {{ venueData.city }},
                        {{ venueData.postcode }}
                    </div>
                </div>

                <div
                    v-if="hasLocation && isMobile && showSecondPart"
                    class="has-location-and-mobile"
                >
                    Mobile area for my service
                    <div class="sub-label-text">
                        My mobile service is in the area around my venue:
                    </div>

                    <div class="">
                        <div class="mt-3 d-flex flex-row justify-content-between">
                            <div
                                @click="mobileServiceSameVenue = 'yes'"
                                class="mr-3 activity-card p-4"
                                :class="{ selected: mobileServiceSameVenue === 'yes' }"
                            >
                                <span>Yes</span>
                            </div>
                            <div
                                @click="mobileServiceSameVenue = 'no'"
                                class="mr-3 activity-card p-3"
                                :class="{ selected: mobileServiceSameVenue === 'no' }"
                            >
                                <span>No, it’s in another area</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- If selected first checkbox -->
                <div v-if="showLocationForm" class="d-flex flex-column">
                    <label class="show my-3"
                    >Venue address<span class="required-field">*</span></label
                    >

                    <VueGoogleAutocomplete
                        ref="autocompleteLocation"
                        id="location"
                        classname="p-2 au-location"
                        placeholder="Search for an address"
                        @placechanged="getAddressData"
                        :fields="['address_components', 'geometry']"
                        :types="'geocode'"
                        :country="['uk']"
                    />

                    <div v-if="venueAutocompleteError">
                        <div class="error-text" data-test="validation-error">{{ venueAutocompleteError }}</div>
                    </div>

                    <div
                        @click="switchManualAddress"
                        class="my-3 cursor-pointer enter-address-manually"
                    >
                        Enter manually
                    </div>
                </div>

                <!-- if selected second checkbox -->
                <div v-if="(advancedForm && showSecondPart) || (!advancedForm && showMobileForm)">
                    <div v-if="!advancedForm || mobileServiceSameVenue === 'no'">
                        <div>
                            <label class="show mt-3"
                            >Postcode<span class="required-field">*</span></label
                            >
                            <div class="sub-label-text">
                                Which postcode is your mobile service based?
                            </div>
                        </div>
                        <VueGoogleAutocomplete
                            ref="autocompleteMobile"
                            id="mobile"
                            classname="p-2 au-mobile"
                            placeholder="Search for an address"
                            @placechanged="getAddressDataMobile"
                            :fields="['address_components', 'geometry']"
                            :types="'geocode'"
                            :country="['uk']"
                        />

                        <div v-if="venueMobileError">
                            <div class="error-text" data-test="validation-error">{{ venueMobileError }}</div>
                        </div>
                    </div>

                    <div class="choose-distance mt-3">
                        <label class="show"
                        >Choose a distance<span class="required-field">*</span></label
                        >
                        <div class="sub-label-text">
                            How far are you willing to travel from this address?
                        </div>

                        <v-select
                            :options="distance"
                            placeholder="Choose a distance"
                            v-model="venueMobile.travelDistance"
                        />
                    </div>
                </div>

                <div v-if="(showLocationForm || showMobileForm) && !enterManually">
                    <GoogleMap
                        ref="map"
                        id="map"
                        @locationChange="updateLatLng"
                        @addressUpdated="addressUpdated"
                        :lat="getLat"
                        :lng="getLng"
                        :radius="getDistance"
                        :zoom="mapZoom"
                        class="mt-4"
                    />
                </div>
            </div>

            <div v-if="enterManually">
                <div class="mt-4 manual-address-container">
                    <div class="mt-4 d-flex flex-column">
                        <InputField
                            labelText="Venue name"
                            placeholder="Enter a venue name"
                            :required="true"
                            v-model="venueData.venueName"
                            :value="venueData.venueName"
                            :error="venueDataError.venueName"
                            :testId="'summary-venue-name-input'"
                            dataTest="input-venue-name"
                        />
                    </div>
                    <div class="mt-4 d-flex flex-column">
                        <InputField
                            labelText="Address"
                            placeholder="Address"
                            :required="true"
                            v-model="venueData.address1"
                            :value="venueData.address1"
                            :error="venueDataError.address1"
                            :testId="'summary-venue-address-input'"
                            dataTest="input-venue-address"
                        />
                    </div>
                    <div v-if="false" class="mt-3 d-flex flex-column">
                        <InputField
                            labelText="Address line 2"
                            placeholder="Address line 2"
                            :required="true"
                            v-model="venueData.address2"
                            :value="venueData.address2"
                        />
                    </div>
                    <div class="mt-4 d-flex flex-column">
                        <InputField
                            labelText="Area"
                            placeholder="Enter an area"
                            :required="true"
                            v-model="venueData.area"
                            :value="venueData.area"
                            :error="venueDataError.area"
                            sub-label-text="Please enter an area name. This will help your customers
                                easily differentiate between multiple venues of your business."
                            :testId="'summary-venue-area-input'"
                            dataTest="input-venue-area"
                        />
                    </div>
                    <div class="mt-4 d-flex flex-column">
                        <InputField
                            labelText="Town/City"
                            placeholder="Enter a town/city"
                            :required="true"
                            v-model="venueData.city"
                            :value="venueData.city"
                            :error="venueDataError.city"
                            :testId="'summary-venue-city-input'"
                            dataTest="input-venue-city"
                        />
                    </div>
                    <div class="mt-4 d-flex flex-column">
                        <InputField
                            labelText="Postcode"
                            placeholder="Enter a postcode"
                            :required="true"
                            v-model="venueData.postcode"
                            :value="venueData.postcode"
                            :error="venueDataError.postcode"
                            :testId="'summary-venue-postcode-input'"
                            dataTest="input-venue-postcode"
                        />
                    </div>
                    <div class="mt-4 d-flex flex-column">
                        <InputField
                            labelText="Country"
                            placeholder="Enter country name"
                            :required="true"
                            v-model="venueData.country"
                            :value="venueData.country"
                            :disabled="true"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.select-all {
    cursor: pointer;
}

.au-location {
    background: #ffffff;
    border: 1px solid #dddddd;
    box-sizing: border-box;
    border-radius: 10px;
}

.au-location:focus,
.au-mobile:focus {
    outline: unset;
}

.au-mobile {
    background: #ffffff;
    border: 1px solid #dddddd;
    box-sizing: border-box;
    border-radius: 10px;
}

input.vs__search {
    display: none;
}

.enter-address-manually {
    font-style: normal;
    font-weight: 700;
    font-size: 12px;
    line-height: 18px;
    color: #260045;
}

.activity-card {
    display: flex;
    flex-direction: row;
    align-items: center;
    text-align: center;
    padding: 24px 24px;
    width: 251.5px;
    background: #ffffff;
    border: 1px solid #dddddd;
    box-sizing: border-box;
    border-radius: 10px;
    cursor: pointer;
}

.activity-card.selected {
    background: #260045 !important;
    color: #fff !important;
}

.choose-distance {
    width: 150px;
}

.has-location-and-mobile {
    width: 459px;
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    color: #260045;
    margin-top: 29px;
}

.sub-label-text {
    font-style: italic;
    font-weight: normal;
    font-size: 14px;
    line-height: 21px;
    color: #636066;
}
</style>
